import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';
import { filterMap } from 'fp-ts-rxjs/Observable';
import { fromEither } from 'fp-ts/Option';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  mergeMap,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { APIV4 } from '../../components/modal';
import { DmbLabel, SessionLabelOption, UserSession, UserSessionC } from '../../model';
import { UserService } from '../../user';
import { ClientService } from '../client/client.service';
import { CommsAuthService, CommsService } from '../comms';
import { skipHttpErrorPage } from '../http-error';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private comms = inject(CommsService);
  private userService = inject(UserService);
  private clientService = inject(ClientService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private commsAuth = inject(CommsAuthService);

  private reloader = new BehaviorSubject(true);
  session = this.reloader.pipe(
    mergeMap(() => this.comms.onMessage<Partial<UserSession>>('/user/session')),
    map(UserSessionC.decode),
    filterMap(fromEither),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  selectedClientId = this.session.pipe(
    map((s) => s.selectedClientId),
    distinctUntilChanged(),
  );

  isImpersonated = this.session.pipe(
    map((s) => s.isImpersonated),
    startWith(false),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  isAdmin = toSignal(this.session.pipe(map((session) => session.privileges.includes('SYSTEM.Developer Tools'))), {
    initialValue: false,
  });
  isBetaTester = toSignal(this.session.pipe(map((session) => session.privileges.includes('Common.Beta Tester'))), {
    initialValue: false,
  });
  betaTesterCanActivate = this.commsAuth.authenticated.pipe(
    switchMap((auth) => {
      if (!auth) {
        return of(new UrlTree());
      }

      return this.session.pipe(map((session) => session.privileges.includes('Common.Beta Tester') || new UrlTree()));
    }),
    catchError(() => of(new UrlTree())),
  );

  unimpersonate = this.http.get<{ userId: number }>(`${APIV4}users/revoke_impersonation`).pipe(
    tap(() => this.userService.reload()),
    tap((res) => this.router.navigate([`/page/USERS.edit/${res.userId}`])),
  );

  impersonate(userId: number) {
    return this.http.post(`${APIV4}users/impersonate`, { userId, context: skipHttpErrorPage() }).pipe(
      tap(() => this.userService.reload()),
      catchError(() => {
        console.error(`something went wrong trying to impersonate user: ${userId}`);
        return [];
      }),
    );
  }

  client = toSignal(
    combineLatest([this.session, this.userService.authenticatedUser]).pipe(
      mergeMap(([session, user]) =>
        this.clientService.clients.pipe(map((clients) => clients[session.selectedClientId || user.clientId])),
      ),
    ),
  );

  labels = this.clientService.labels;

  labelOption = combineLatest([this.session, this.labels]).pipe(
    map(([session, lbls]): SessionLabelOption => {
      const lbl = session.selectedLabelId ? lbls.find((l) => l.id === session.selectedLabelId) : undefined;
      return {
        selectable: lbls.length > 1,
        name: lbl ? lbl.name : lbls.length > 1 ? $localize`:@@Common.label.all-labels:All Labels` : '',
        label: lbl || null,
      };
    }),
  );

  async unsetClient() {
    this.switchClient(await firstValueFrom(this.clientService.client));
  }

  switchClient(client: { id: number }) {
    this.comms.send('/session/update', { selectedClient: client.id });
  }

  switchLabel(newLabel: DmbLabel) {
    this.comms.send('/session/update', { selectedLabel: newLabel.id });
  }

  unsetLabel() {
    this.comms.send('/session/update', { unsetLabel: true });
  }

  async logout() {
    return firstValueFrom(
      this.isImpersonated.pipe(switchMap((impersonated) => (impersonated ? this.unimpersonate : this.doLogout()))),
    );
  }

  private async doLogout() {
    this.userService.logout();
    return EMPTY;
  }
}
